package com.stiv_apps.facecutweb.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.stiv_apps.facecutweb.style.TextSelectionStyle
import com.stiv_apps.facecutweb.util.BackgroundGradient
import com.stiv_apps.facecutweb.util.Constants.SF_PRO_LIGHT
import com.stiv_apps.facecutweb.util.Constants.SF_PRO_MEDIUM
import com.stiv_apps.facecutweb.util.TextGradient
import com.stiv_apps.facecutweb.util.Theme
import com.varabyte.kobweb.compose.css.BackgroundClip
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.background
import com.varabyte.kobweb.compose.ui.modifiers.backgroundImage
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.layout.Surface
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vw
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

@OptIn(DelicateApi::class)
@Composable
@Page
fun PrivacyPolicyPage() {
    val breakpoint = rememberBreakpoint()
    val largeBreakpoint = remember(breakpoint) {
        breakpoint > Breakpoint.LG
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Colors.Black)
            .width(100.vw),
        contentAlignment = Alignment.TopCenter
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .maxWidth(100.percent)
                .backgroundImage(BackgroundGradient)
                .padding(
                    leftRight = if (breakpoint > Breakpoint.LG) 120.px
                    else if (breakpoint == Breakpoint.LG) 100.px
                    else if (breakpoint > Breakpoint.MD) 70.px
                    else if (breakpoint == Breakpoint.MD) 50.px
                    else 40.px
                ),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .maxWidth(800.px)
                    .padding(bottom = 100.px),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                // Main Title
                H1(
                    attrs = TextSelectionStyle.toModifier()
                        .fillMaxWidth()
                        .margin(bottom = 20.px, top = if (largeBreakpoint) 50.px else 20.px)
                        .backgroundImage(TextGradient)
                        .background { this.clip(BackgroundClip.Text) }
                        .color(Color.transparent)
                        .fontFamily(SF_PRO_MEDIUM)
                        .fontSize(if (largeBreakpoint) 4.cssRem else 3.cssRem)
                        .lineHeight(1.2)
                        .textAlign(TextAlign.Center)
                        .toAttrs()
                ) {
                    Text("Privacy Policy")
                }

                // Subtitle Information
                P(
                    attrs = TextSelectionStyle.toModifier()
                        .fillMaxWidth()
                        .margin(bottom = 40.px)
                        .color(Theme.TextLowVisibility.rgb)
                        .fontFamily(SF_PRO_LIGHT)
                        .fontSize(1.1.cssRem)
                        .lineHeight(1.4)
                        .textAlign(TextAlign.Center)
                        .toAttrs()
                ) {
                    Text("Effective Date: 26.10.2025")
                }

                Surface(modifier = Modifier.height(20.px)) {}

                // Introduction
                P(
                    attrs = TextSelectionStyle.toModifier()
                        .fillMaxWidth()
                        .margin(bottom = 40.px)
                        .color(Theme.Text.rgb)
                        .fontFamily(SF_PRO_LIGHT)
                        .fontSize(1.1.cssRem)
                        .lineHeight(1.6)
                        .textAlign(TextAlign.Start)
                        .toAttrs()
                ) {
                    Text("FaceCut (\"we,\" \"our,\" or \"us\") respects your privacy. This Privacy Policy explains how we collect, use, and protect your information when you use our mobile application (\"App\").")
                }

                // Section 1
                SectionTitle("1. Information We Collect", largeBreakpoint)
                SubSectionTitle("a. Account Information", largeBreakpoint)
                SectionContent(
                    "If you sign in using a third-party provider (e.g., Google, Apple, or other OAuth services), we collect:",
                )
                SectionContent("• Your first and last name")
                SectionContent("• Your email address")
                SectionContent(
                    "This data is securely stored on our backend to maintain your account and provide app functionality.",
                )

                Surface(modifier = Modifier.height(20.px)) {}

                SubSectionTitle("b. Uploaded Images", largeBreakpoint)
                SectionContent(
                    "When you upload or edit photos within FaceCut:",
                )
                SectionContent(
                    "- The images you upload are processed to remove backgrounds, add outlines, or apply AI-based edits (such as expression adjustments).",
                )
                SectionContent(
                    "- These images are securely stored on encrypted servers and associated with your user account.",
                )
                SectionContent(
                    "- You have full control to delete any uploaded image at any time from within the app.",
                )

                // Section 2
                SectionTitle("2. How We Use Your Information", largeBreakpoint)
                SectionContent("We use the information we collect to:")
                SectionContent(
                    "- Provide and improve app functionality",
                )
                SectionContent(
                    "- Maintain your user account and authentication",
                )
                SectionContent(
                    "- Process and store uploaded images for editing and user access",
                )
                SectionContent(
                    "- Respond to support requests and account inquiries",
                )
                SectionContent(
                    "We do not sell, rent, or trade your personal information or uploaded content to third parties.",
                )

                // Section 3
                SectionTitle("3. Your Rights and Choices", largeBreakpoint)
                SubSectionTitle("a. Delete Uploaded Images", largeBreakpoint)
                SectionContent(
                    "You can delete any photo you upload within the app at any time. Once deleted, it will be permanently removed from our servers.",
                )

                Surface(modifier = Modifier.height(15.px)) {}

                SubSectionTitle("b. Delete Account", largeBreakpoint)
                SectionContent(
                    "You can request to delete your account directly from within the app. When you do, all associated data (including your name, email address, and uploaded images) will be permanently deleted from our servers.",
                )

                // Section 4
                SectionTitle("4. Data Security", largeBreakpoint)
                SectionContent(
                    "We use industry-standard security measures, including encryption and secure connections (HTTPS), to protect your data.",
                )
                SectionContent(
                    "Uploaded images are encrypted and stored on trusted cloud servers. We take all reasonable measures to prevent unauthorized access, use, or disclosure.",
                )

                // Section 5
                SectionTitle("5. Third-Party Services", largeBreakpoint)
                SectionContent(
                    "FaceCut may use third-party services to provide certain features (such as authentication or AI image processing).",
                )
                SectionContent(
                    "These services are governed by their own privacy policies, which we encourage you to review.",
                )
                SectionContent("Examples include:")
                SectionContent("- Google Sign-In (Google OAuth)")
                SectionContent("- Apple Sign-In (Apple OAuth)")
                SectionContent(
                    "We do not share user-uploaded images or personal data with these third-party providers unless required to operate the core features of our service.",
                )

                // Section 6
                SectionTitle("6. Children's Privacy", largeBreakpoint)
                SectionContent(
                    "FaceCut is not intended for children under 13. We do not knowingly collect personal information from children. If such information is collected unintentionally, we will promptly delete it.",
                )

                // Section 7
                SectionTitle("7. Changes to This Policy", largeBreakpoint)
                SectionContent(
                    "We may update this Privacy Policy from time to time. Any updates will be reflected by the \"Effective Date\" above. Continued use of the app after changes are made means you accept the updated policy.",
                )

                // Section 8
                SectionTitle("8. Device Information Collection", largeBreakpoint)
                SectionContent(
                    "We collect and store a unique device identifier to:",
                )
                SectionContent(
                    "- Prevent abuse and fraudulent account creation",
                )
                SectionContent(
                    "- Ensure fair distribution of free credits",
                )
                SectionContent(
                    "- Maintain the security and integrity of our service",
                )
                SectionContent("This identifier is:")
                SectionContent(
                    "- Stored securely in our Firebase database",
                )
                SectionContent("- Not shared with third parties")
                SectionContent(
                    "- Not used for advertising or tracking purposes",
                )
                SectionContent(
                    "- Used solely for fraud prevention and service functionality",
                )

                // Section 9
                SectionTitle(
                    "9. Face Data Handling (AI Image Processing)",
                    largeBreakpoint
                )
                SectionContent(
                    "FaceCut processes photos that may include human faces solely for the purpose of performing the edits you request — such as background removal, facial expression modification, or outline enhancement.",
                )
                SectionContent(
                    "We do not collect, analyze, or store biometric identifiers, facial geometry, or facial recognition data.",
                )
                SectionContent(
                    "Uploaded photos are securely stored on encrypted servers linked to your user account and remain available until you decide to delete them.",
                )
                SectionContent(
                    "You can delete any uploaded photo at any time, and once deleted, it is permanently removed from our servers.",
                )
                SectionContent(
                    "We do not share, sell, or disclose uploaded files or any derived data to third parties.",
                )

                // Section 10
                SectionTitle("10. Contact Us", largeBreakpoint)
                SectionContent(
                    "If you have any questions, requests, or concerns about this Privacy Policy, please contact us at:",
                )
                P(
                    attrs = TextSelectionStyle.toModifier()
                        .fillMaxWidth()
                        .margin(topBottom = 10.px)
                        .color(Theme.Text.rgb)
                        .fontFamily(SF_PRO_MEDIUM)
                        .fontSize(1.1.cssRem)
                        .lineHeight(1.6)
                        .textAlign(TextAlign.Start)
                        .toAttrs()
                ) {
                    Text("support@facecut.app")
                }
            }
        }
    }
}

@Composable
private fun SectionTitle(
    text: String,
    largeBreakpoint: Boolean,
) {
    Surface(modifier = Modifier.height(30.px)) {}
    H2(
        attrs = TextSelectionStyle.toModifier()
            .fillMaxWidth()
            .margin(bottom = 20.px)
            .color(Theme.Text.rgb)
            .fontFamily(SF_PRO_MEDIUM)
            .fontSize(if (largeBreakpoint) 1.8.cssRem else 1.6.cssRem)
            .lineHeight(1.3)
            .textAlign(TextAlign.Start)
            .toAttrs()
    ) {
        Text(text)
    }
}

@Composable
private fun SubSectionTitle(
    text: String,
    largeBreakpoint: Boolean,
) {
    Surface(modifier = Modifier.height(15.px)) {}
    H3(
        attrs = TextSelectionStyle.toModifier()
            .fillMaxWidth()
            .margin(bottom = 15.px)
            .color(Theme.Text.rgb)
            .fontFamily(SF_PRO_MEDIUM)
            .fontSize(if (largeBreakpoint) 1.4.cssRem else 1.3.cssRem)
            .lineHeight(1.3)
            .textAlign(TextAlign.Start)
            .toAttrs()
    ) {
        Text(text)
    }
}

@Composable
private fun SectionContent(
    text: String,
) {
    P(
        attrs = TextSelectionStyle.toModifier()
            .fillMaxWidth()
            .margin(bottom = 15.px)
            .color(Theme.TextLowVisibility.rgb)
            .fontFamily(SF_PRO_LIGHT)
            .fontSize(1.1.cssRem)
            .lineHeight(1.6)
            .textAlign(TextAlign.Start)
            .toAttrs()
    ) {
        Text(text)
    }
}